import React, { useEffect, useState } from "react";
import {
  Sparkles,
  Github,
  Download,
  Zap,
  Layout,
  Smartphone,
} from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../service/firebase"; // adjust path if needed
import "../styles/About.css";

const SKILLS = [
  { name: "Angular", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg" },
  { name: "React", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
  { name: "Ionic", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ionic/ionic-original.svg" },
  { name: "MySQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" },
  { name: "NoSQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
  { name: "Java", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" },
  { name: "Firebase", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg" },
  { name: "Git", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg" },
  { name: "JavaScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" },
  { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
  { name: "Redux", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg" },
  { name: "REST API", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg" },
];

const About = () => {
  const [floatingSkills, setFloatingSkills] = useState([]);
  const [aboutData, setAboutData] = useState({
    experienceTitle: "",
    description: "",
    githubUrl: "",
    resumeUrl: "",
    stats: {
      years: "",
      projects: "",
      contributions: "",
    },
  });

  // Convert Google Drive share link → downloadable link
  const getDownloadableDriveUrl = (url) => {
    if (!url) return "";
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!match) return url;
    return `https://drive.google.com/uc?export=download&id=${match[1]}`;
  };

  // Floating skills animation (unchanged)
  useEffect(() => {
    const skills = SKILLS.map((skill, idx) => ({
      id: idx,
      icon: skill.icon,
      name: skill.name,
      left: Math.random() * 85 + 5,
      top: Math.random() * 80 + 5,
      duration: Math.random() * 8 + 12,
      delay: Math.random() * 2,
    }));
    setFloatingSkills(skills);
  }, []);

  // Fetch About data from Firebase
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const docRef = doc(db, "Portfolio", "About");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAboutData(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching About data:", error);
      }
    };
    fetchAboutData();
  }, []);

  return (
    <section id="about" className="about-main-section">
      {/* Background Orbs */}
      <div className="about-ambient-layer">
        <div className="about-orb about-orb-1" />
        <div className="about-orb about-orb-2" />
      </div>

      {/* Floating Icons */}
      <div className="about-floating-container" aria-hidden="true">
        {floatingSkills.map((skill) => (
          <div
            key={skill.id}
            className="about-floater"
            style={{
              left: `${skill.left}%`,
              top: `${skill.top}%`,
              animationDuration: `${skill.duration}s`,
              animationDelay: `${skill.delay}s`,
            }}
            title={skill.name}
          >
            <img src={skill.icon} alt={skill.name} />
          </div>
        ))}
      </div>

      <div className="about-content-wrapper">
        <div className="about-hero-header">
          <div className="about-pill-badge">
            <Sparkles size={16} />
            <span>Full Stack Developer</span>
          </div>

          <h1 className="about-hero-title">
            Rohith <span className="about-text-gradient">Muthusamy</span>
          </h1>

          <div className="about-bio-container">
            <p className="about-exp-badge">
              {aboutData.experienceTitle}
            </p>
            <p className="about-bio-text">
              {aboutData.description}
            </p>
          </div>

          <div className="about-actions">
            <a
              href={aboutData.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="about-cta-btn btn-filled"
            >
              <Github size={18} />
              <span>GitHub</span>
            </a>

            <a
              href={getDownloadableDriveUrl(aboutData.resumeUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="about-cta-btn btn-outlined"
            >
              <Download size={18} />
              <span>Resume</span>
            </a>
          </div>
        </div>

        {/* Stats Card (NOW FROM DB) */}
        <div className="about-grid-row">
          <div className="about-glass-card stats-box">
            <div className="about-card-heading">Impact & Experience</div>
            <div className="about-stats-flex">
              <div className="about-stat-unit">
                <div className="stat-number">{aboutData.stats.years}+</div>
                <div className="stat-caption">Years</div>
              </div>
              <div className="about-stat-unit">
                <div className="stat-number">{aboutData.stats.projects}+</div>
                <div className="stat-caption">Personal Projects</div>
              </div>
              <div className="about-stat-unit">
                <div className="stat-number">{aboutData.stats.contributions}+</div>
                <div className="stat-caption">Contributions</div>
              </div>
            </div>
          </div>

          {/* Values Card (unchanged) */}
          <div className="about-glass-card values-box">
            <div className="about-card-heading">Core Values</div>
            <div className="about-values-flex">
              <div className="about-value-unit">
                <div className="value-icon-circle">
                  <Zap size={24} />
                </div>
                <span className="value-caption">Performance</span>
              </div>
              <div className="about-value-unit">
                <div className="value-icon-circle">
                  <Layout size={24} />
                </div>
                <span className="value-caption">UI/UX</span>
              </div>
              <div className="about-value-unit">
                <div className="value-icon-circle">
                  <Smartphone size={24} />
                </div>
                <span className="value-caption">Quality Code</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
